<template>
    <div class="project-brand-mgmt p-4">
        <div class="d-flex align-items-center mb-2">
            <h4 class="mb-0">메일 브랜드(프로젝트) 관리</h4>
            <b-button size="sm" variant="outline-secondary" class="ml-3" @click="reload">새로고침</b-button>
        </div>
        <p class="text-muted small mb-3">
            메일 브랜드를 <b>접속 주소가 아니라 프로젝트로</b> 판별합니다. 프로젝트별 브랜드를 지정하세요.<br>
            아래 목록에는 <b>전체 프로젝트</b>가 나옵니다.
            <b-badge variant="light" class="border text-muted font-weight-normal">미설정</b-badge>
            프로젝트는 잘못된 브랜드로 발송될 수 있으니, 화면에서 확인해 브랜드를 지정하세요.
        </p>

        <!-- 새 매핑 추가 / 갱신 -->
        <b-card class="mb-3" body-class="py-3">
            <b-form-row class="align-items-end">
                <b-col md="5">
                    <label class="small mb-1">프로젝트 코드</label>
                    <b-form-input v-model.trim="newCode" placeholder="예: HD_DOM_HQ_25" size="sm" />
                </b-col>
                <b-col md="4">
                    <label class="small mb-1">브랜드</label>
                    <b-form-select v-model="newBrand" :options="brandOptions" size="sm" />
                </b-col>
                <b-col md="3">
                    <b-button variant="primary" size="sm" block :disabled="!newCode || !newBrand"
                              @click="saveMapping(newCode, newBrand, true)">
                        추가 / 저장
                    </b-button>
                </b-col>
            </b-form-row>
        </b-card>

        <!-- 매핑 목록 (백엔드가 준 최신순 정렬을 그대로 사용) -->
        <b-table
            :items="rows"
            :fields="fields"
            :busy="loading"
            small
            hover
            responsive
            show-empty
            empty-text="표시할 프로젝트가 없습니다."
        >
            <template #table-busy>
                <div class="text-center my-3"><b-spinner small /> 불러오는 중…</div>
            </template>
            <template #cell(brand)="row">
                <div class="d-flex align-items-center flex-wrap" style="gap: 6px;">
                    <b-form-select
                        v-model="row.item.selectedBrand"
                        :options="brandOptions"
                        size="sm"
                        style="max-width: 200px"
                        :class="{ 'unmapped-select': !row.item.mapped }"
                    />
                    <b-badge
                        v-if="!row.item.mapped"
                        variant="light"
                        class="border text-muted font-weight-normal"
                    >
                        미설정 · 기본 {{ DEFAULT_BRAND }}
                    </b-badge>
                </div>
            </template>
            <template #cell(updatedAt)="row">
                <span class="small text-muted">{{ formatTs(row.item.updatedAt) }}</span>
            </template>
            <template #cell(actions)="row">
                <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="saveMapping(row.item.projectCode, row.item.selectedBrand, false)"
                >
                    저장
                </b-button>
            </template>
        </b-table>

        <p class="small text-muted mt-2">
            지정 가능한 브랜드는 등록된 메일 템플릿의 sub_domain 값으로 한정됩니다
            ({{ brands.join(', ') || '로딩 중…' }}).
        </p>
    </div>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
export default {
    name: 'ProjectBrandManagement',
    data() {
        return {
            // 미매핑 프로젝트에 화면상 미리 표시할 기본 브랜드.
            // 백엔드는 미매핑 시 brand=null 로만 내려주므로 기본값 표기는 프론트 책임이다.
            DEFAULT_BRAND: 'qesg',
            rows: [],
            brands: [],
            loading: false,
            newCode: '',
            newBrand: null,
            // 정렬은 백엔드가 준 순서(프로젝트 id 내림차순=최신순)를 그대로 따르므로 sortable 미지정.
            fields: [
                { key: 'projectCode', label: '프로젝트 코드' },
                { key: 'brand', label: '브랜드' },
                { key: 'updatedAt', label: '수정 시각' },
                { key: 'actions', label: '', class: 'text-right' },
            ],
        };
    },
    computed: {
        brandOptions() {
            return [
                { value: null, text: '브랜드 선택…', disabled: true },
                ...this.brands.map((b) => ({ value: b, text: b })),
            ];
        },
    },
    created() {
        this.fetchVocabulary();
        this.fetchRows();
    },
    methods: {
        async reload() {
            await Promise.all([this.fetchVocabulary(), this.fetchRows()]);
        },
        // 백엔드가 ApiResponse 봉투({success, code, data})로 응답하면 payload 는 data.data.
        // 혹시 인터셉터가 이미 벗겼으면(raw) 그대로 쓴다.
        _payload(data) {
            if (data && typeof data === 'object' && !Array.isArray(data)
                && 'success' in data && 'data' in data) {
                return data.data;
            }
            return data;
        },
        async fetchVocabulary() {
            try {
                const { data } = await this.$axios.get('/admin/project-brands/vocabulary');
                const p = this._payload(data);
                this.brands = (p && p.brands) || [];
            } catch (e) {
                this.toastErr('브랜드 목록을 불러오지 못했습니다.');
            }
        },
        async fetchRows() {
            this.loading = true;
            try {
                const { data } = await this.$axios.get('/admin/project-brands');
                const p = this._payload(data);
                const arr = Array.isArray(p) ? p : [];
                this.rows = arr.map((r) => {
                    // 신 백엔드는 mapped 를 명시(미매핑=false, brand=null)한다.
                    // 구 백엔드(mapped 필드 없음, 매핑된 항목만 반환)는 brand 존재로 매핑 여부를
                    // 추론해 기존 동작을 유지한다(하위호환).
                    const isMapped = typeof r.mapped === 'boolean' ? r.mapped : !!r.brand;
                    return {
                        ...r,
                        mapped: isMapped,
                        // 미설정 행은 기본 브랜드를 미리 표시(운영자가 그대로/변경하여 등록).
                        selectedBrand: r.brand || this.DEFAULT_BRAND,
                    };
                });
            } catch (e) {
                this.toastErr('매핑 목록을 불러오지 못했습니다.');
            } finally {
                this.loading = false;
            }
        },
        async saveMapping(code, brand, isNew) {
            const c = (code || '').trim();
            if (!c) { this.toastErr('프로젝트 코드를 입력하세요.'); return; }
            if (!brand) { this.toastErr('브랜드를 선택하세요.'); return; }
            try {
                await this.$axios.put(`/admin/project-brands/${encodeURIComponent(c)}`, { brand });
                this.toastOk(`${c} → ${brand} 저장됨`);
                if (isNew) { this.newCode = ''; this.newBrand = null; }
                await this.fetchRows();
            } catch (e) {
                const msg = getErrorMessage(e, '저장에 실패했습니다.');
                this.toastErr(msg);
                await this.fetchRows(); // 실패 시 화면 값 원복
            }
        },
        formatTs(ts) {
            if (!ts) return '-';
            try {
                return new Date(ts).toLocaleString('ko-KR');
            } catch (e) {
                return ts;
            }
        },
        toastOk(msg) {
            this.$bvToast.toast(msg, { title: '완료', variant: 'success', autoHideDelay: 2500 });
        },
        toastErr(msg) {
            this.$bvToast.toast(msg, { title: '오류', variant: 'danger' });
        },
    },
};
</script>

<style scoped>
.project-brand-mgmt {
    max-width: 900px;
}

/* 미설정(미매핑) 행의 드롭다운은 회색으로 표시해 실제 지정값과 구분한다. */
.unmapped-select {
    color: #6c757d;
    font-style: italic;
}
</style>
